import {
  AuditNote,
  Column,
  Container,
  EmptyState,
  Row
} from '@cdab/scania/qpr/components'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import { pledgeController } from '@cdab/scania/qpr/offline/controllers'
import parse from 'html-react-parser'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import {
  isRouteErrorResponse,
  useParams,
  useRouteError,
  useRouteLoaderData
} from 'react-router-dom'
import styled from 'styled-components'
import invariant from 'tiny-invariant'

import { isNumber } from '@cdab/utils'
import { TdsAccordion, TdsAccordionItem, TdsBlock } from '@scania/tegel-react'
import type { PledgeLoaderData } from '../pledge-base-view'

const StyledRow = styled(Row)`
  margin-top: var(--tds-spacing-element-16);
`

const StyledAccordionItem = styled(TdsAccordionItem)`
  > :first-child {
    margin-top: 0;
  }
`

type Params = {
  auditId: string
  pledgeId: string
}

export const GeneralTab = observer(() => {
  const { t } = useTranslation('audit')
  const { auditId: sAuditId } = useParams<Params>()

  const auditId = Number.parseInt(sAuditId ?? '')
  invariant(
    isNumber(auditId),
    `GeneralTab: expected auditId to be number but was: ${auditId}`
  )

  const { pledge, perspectiveGuids, auditIsReadonly } = useRouteLoaderData(
    'pledge'
  ) as PledgeLoaderData

  const onPledgeNoteSaved = (note: string) => {
    pledgeController.SaveNote(auditId, pledge.id, note)
  }

  return (
    <Container>
      <StyledRow>
        <Column width={12} lg={6} fullHeight>
          <div className='tds-mode-variant-secondary'>
            <TdsBlock componentTag='div'>
              <TdsAccordion>
                <StyledAccordionItem header={t('keyword')}>
                  {parse(t(perspectiveGuids.keyword))}
                </StyledAccordionItem>
                <StyledAccordionItem header={t('customer-perspective')}>
                  {parse(t(perspectiveGuids.customer))}
                </StyledAccordionItem>
                <StyledAccordionItem header={t('dealer-perspective')}>
                  {parse(t(perspectiveGuids.dealer))}
                </StyledAccordionItem>
              </TdsAccordion>
            </TdsBlock>
          </div>
        </Column>
        <Column width={12} lg={6}>
          <div className='tds-mode-variant-secondary'>
            <TdsBlock>
              <AuditNote
                disabled={auditIsReadonly}
                note={pledge.note ?? ''}
                onSaveNote={onPledgeNoteSaved}
                modeVariant='primary'
              />
            </TdsBlock>
          </div>
        </Column>
      </StyledRow>
    </Container>
  )
})

export function ErrorBoundary() {
  const { t } = useTranslation('errors')
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    return (
      <Container
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column width={12} padding={isLg}>
            <EmptyState
              title={t('pledge-not-found')}
              description={t('talk-to-admin')}
            />
          </Column>
        </Row>
      </Container>
    )
  }

  // rethrow to let the parent error boundary handle it
  // when it's not a special case for this route
  throw error
}
